import { useEffect, useState } from 'react';

import { FormattedDate, FormattedMessage } from 'react-intl';

import type { InitialStatePostingRestriction } from 'mastodon/initial_state';
import { postingRestrictions } from 'mastodon/initial_state';

import { WarningMessage } from './warning';

// How often to check whether a period started or ended
const TICK = 15_000;

// Periods are set in Korean time on the admin page, so they are shown the
// same way here
const TIME_ZONE = 'Asia/Seoul';

const minuteOfDayFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** Minutes after midnight in Korean time */
const minuteOfDay = (now: number) => {
  const parts = minuteOfDayFormat.formatToParts(now);
  const value = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);

  return value('hour') * 60 + value('minute');
};

/** Same rule as PostingRestrictionPeriod.covers? on the server */
const coversTime = (period: InitialStatePostingRestriction, now: number) => {
  if (now < Date.parse(period.starts_at) || now >= Date.parse(period.ends_at)) {
    return false;
  }

  const { daily_starts_at: start, daily_ends_at: end } = period;
  if (start === null || start === undefined || end === null || end === undefined) {
    return true;
  }

  const minute = minuteOfDay(now);

  // A window like 23:00–02:00 runs past midnight
  return start < end
    ? start <= minute && minute < end
    : minute >= start || minute < end;
};

export const activeRestrictions = (
  periods: InitialStatePostingRestriction[],
  now: number,
) => periods.filter((period) => coversTime(period, now));

const PeriodTime: React.FC<{ value: string }> = ({ value }) => (
  <FormattedDate
    value={value}
    month='short'
    day='numeric'
    hour='2-digit'
    minute='2-digit'
    timeZone={TIME_ZONE}
  />
);

const clock = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/**
 * Tells the author that new posts are paused (Administration > Posting
 * restrictions), in the same style as the other notes above the compose
 * form. The server enforces it; this only explains what will happen.
 *
 * DM admin-copy periods are not announced here: the copy is added by the
 * server and shows in the post itself.
 */
export const PostingRestrictionNotice: React.FC = () => {
  const [now, setNow] = useState(() => Date.now());

  const publicBans = postingRestrictions.filter(
    (period) => period.kind === 'public_ban',
  );

  useEffect(() => {
    if (publicBans.length === 0) return;

    const timer = setInterval(() => {
      setNow(Date.now());
    }, TICK);

    return () => {
      clearInterval(timer);
    };
  }, [publicBans.length]);

  const active = activeRestrictions(publicBans, now);

  if (active.length === 0) return null;

  return (
    <>
      {active.map((period) => (
        <WarningMessage key={`${period.kind}-${period.starts_at}`}>
          {typeof period.daily_starts_at === 'number' &&
          typeof period.daily_ends_at === 'number' ? (
            <FormattedMessage
              id='compose_form.posting_restriction.public_ban_daily'
              defaultMessage='New posts are paused: you can reply to existing posts or send a private mention. ({start} ~ {end}, every day {window})'
              values={{
                start: <PeriodTime value={period.starts_at} />,
                end: <PeriodTime value={period.ends_at} />,
                window: `${clock(period.daily_starts_at)}~${clock(period.daily_ends_at)}`,
              }}
            />
          ) : (
            <FormattedMessage
              id='compose_form.posting_restriction.public_ban'
              defaultMessage='New posts are paused: you can reply to existing posts or send a private mention. ({start} ~ {end})'
              values={{
                start: <PeriodTime value={period.starts_at} />,
                end: <PeriodTime value={period.ends_at} />,
              }}
            />
          )}
        </WarningMessage>
      ))}
    </>
  );
};
